/**
 * MultiSelectFilter Component
 * 
 * A styled multi-select dropdown filter with search, virtualization, and chip display.
 * Updated to match the new design system aesthetic.
 */

import {
    Menu,
    MenuItem,
    Checkbox,
    ClickAwayListener,
    Box,
    Typography,
    CircularProgress,
    Chip,
    Stack,
    Popover,
    Divider,
    Select,
    FormControl,
    InputLabel,
    OutlinedInput,
    alpha,
    useMediaQuery,
    useTheme,
    Drawer,
    IconButton,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { FixedSizeList } from 'react-window';
import { useMemo, useState, useEffect, useRef } from 'react';
import { SearchBar } from './SearchBar';
import { colors, typography as typographyTokens, radii } from '@components/design-system/tokens';

const ITEM_HEIGHT = 40;

type MultiSelectValue = string | number | object;

interface MultiSelectFilterProps<Value extends MultiSelectValue> {
    label: string;
    idKey?: string;
    disabledWithClear?: boolean;
    onClear?: () => void;
    options: Value[];
    value: Record<string, boolean>;
    onChange: (id: Value) => void;
    getLabelValue: (option: Value) => string;
    disabled?: boolean;
    sort?: boolean;
    margin?: boolean;
    showChips?: boolean;
    search?: boolean;
}

export const MultiSelectFilter = <Value extends MultiSelectValue>({
    label,
    idKey,
    disabled,
    disabledWithClear,
    onClear,
    search,
    options,
    value,
    onChange,
    getLabelValue,
    sort = true,
    margin = true,
    showChips = true,
}: MultiSelectFilterProps<Value>) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(null);
    const [searchText, setSearchText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [displayOptions, setDisplayOptions] = useState<Value[]>([]);
    const [menuWidth, setMenuWidth] = useState(250);
    const [menuHeight, setMenuHeight] = useState(400);
    const menuRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<FixedSizeList>(null);
    const [chipAnchor, setChipAnchor] = useState<HTMLElement | null>(null);
    
    // Fixed height to prevent jumping when results change
    const FIXED_MENU_HEIGHT = isMobile ? 280 : 320;

    const selected = useMemo(
        () => options.filter((option: any) => value[idKey ? (option as any)[idKey] : option]),
        [options, value, idKey]
    );

    const filteredOptions = useMemo(() => {
        const text = searchText.toLowerCase().trim();
        if (!text) return options;
        return options.filter((option: any) => getLabelValue(option).toLowerCase().includes(text));
    }, [searchText, options, getLabelValue]);

    const sortedOptions = useMemo(() => {
        if (!sort) return filteredOptions;
        return [...filteredOptions].sort((a, b) => {
            const aSelected = value[idKey ? (a as any)[idKey] : a] ? 1 : 0;
            const bSelected = value[idKey ? (b as any)[idKey] : b] ? 1 : 0;
            if (bSelected !== aSelected) return bSelected - aSelected;
            return getLabelValue(a).localeCompare(getLabelValue(b));
        });
    }, [filteredOptions, value, idKey, getLabelValue, sort]);

    useEffect(() => {
        if (dropdownAnchor) {
            setIsLoading(true);
            const anchorWidth = dropdownAnchor.getBoundingClientRect().width;
            setMenuWidth(Math.max(240, anchorWidth));
            requestAnimationFrame(() => {
                setDisplayOptions(sortedOptions);
                setIsLoading(false);
                (listRef.current as any)?.resetAfterIndex?.(0);
            });
        } else {
            setSearchText('');
        }
    }, [dropdownAnchor, sortedOptions]);

    useEffect(() => {
        if (!isLoading) {
            // Use fixed height to prevent menu jumping during search
            setMenuHeight(FIXED_MENU_HEIGHT);
        }
    }, [displayOptions, isLoading, FIXED_MENU_HEIGHT]);

    // Forward the entire option object to the parent so consumers can access additional fields (e.g. companyId, modelNumber)
    const handleCheckboxChange = (option: Value) => onChange(option);

    // Pluralize label properly (handles irregular plurals)
    const pluralizeLabel = (text: string) => {
        if (text.endsWith('s') || text.endsWith('x') || text.endsWith('z') || text.endsWith('ch') || text.endsWith('sh')) {
            return `${text}es`;
        }
        // Words ending in consonant + y → replace y with ies (Company → Companies)
        const vowels = ['a', 'e', 'i', 'o', 'u'];
        if (text.endsWith('y') && text.length > 1 && !vowels.includes(text[text.length - 2].toLowerCase())) {
            return `${text.slice(0, -1)}ies`;
        }
        return `${text}s`;
    };

    // Get display text for the select
    const getDisplayText = () => {
        if (selected.length === 0) {
            return `All ${pluralizeLabel(label)}`;
        }
        if (selected.length === 1) {
            return getLabelValue(selected[0]);
        }
        return `${selected.length} selected`;
    };

    const ItemRenderer = ({ index, style }: { index: number; style: React.CSSProperties }) => {
        const option = displayOptions[index];
        const optionId = idKey ? (option as any)[idKey] : option;
        const checked = Boolean(value[optionId]);
        return (
            <MenuItem
                onClick={() => handleCheckboxChange(option as Value)}
                style={{ ...style, padding: 0 }}
                disableRipple
                sx={{
                    '&:hover': {
                        backgroundColor: colors.neutral[100],
                    },
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', px: 1.5, py: 0.5 }}>
                    <Checkbox
                        size='small'
                        disableRipple
                        checked={checked}
                        onChange={() => handleCheckboxChange(option as Value)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            color: colors.neutral[400],
                            '&.Mui-checked': {
                                color: colors.primary[500],
                            },
                        }}
                    />
                    <Typography 
                        noWrap 
                        sx={{ 
                            fontSize: typographyTokens.fontSize.sm,
                            color: colors.neutral[700],
                        }}>
                        {getLabelValue(option)}
                    </Typography>
                </Box>
            </MenuItem>
        );
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden', boxSizing: 'border-box' }}>
            {/* Label */}
            <Typography
                component='label'
                sx={{
                    display: 'block',
                    fontSize: typographyTokens.fontSize.sm,
                    fontWeight: typographyTokens.fontWeight.medium,
                    color: colors.neutral[500],
                    mb: '6px',
                }}>
                {label}
            </Typography>

            {/* Select Trigger */}
            <Box
                    onClick={(e) => {
                    if (disabled && !disabledWithClear) return;
                    if (disabledWithClear) {
                        onClear?.();
                        return;
                    }
                        setDropdownAnchor(dropdownAnchor ? null : e.currentTarget);
                    }}
                // Only enable hover interactions on desktop
                onMouseEnter={isMobile ? undefined : (e) => selected.length > 0 && setChipAnchor(e.currentTarget)}
                onMouseLeave={isMobile ? undefined : (e) => {
                        const related = e.relatedTarget as HTMLElement;
                        if (!related?.closest('.chip-hover-bridge, .chip-popover')) setChipAnchor(null);
                    }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    px: 1.5,
                    py: 1,
                    borderRadius: `${radii.md}px`,
                    border: `1px solid ${dropdownAnchor ? colors.primary[500] : colors.neutral[300]}`,
                    backgroundColor: colors.neutral[0],
                    cursor: disabled && !disabledWithClear ? 'not-allowed' : 'pointer',
                    opacity: disabled && !disabledWithClear ? 0.6 : 1,
                    transition: 'all 150ms ease',
                    '&:hover': {
                        borderColor: disabled && !disabledWithClear ? colors.neutral[300] : colors.neutral[400],
                    },
                }}>
                <Typography
                    noWrap
                    sx={{
                        fontSize: typographyTokens.fontSize.sm,
                        color: selected.length > 0 ? colors.neutral[800] : colors.neutral[500],
                        flex: 1,
                    }}>
                    {getDisplayText()}
                </Typography>
                <KeyboardArrowDownIcon 
                    sx={{ 
                        fontSize: 20, 
                        color: colors.neutral[400],
                        transform: dropdownAnchor ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 150ms ease',
                    }} 
                />
            </Box>

            {/* Chip Popover on Hover - Desktop only */}
            {!isMobile && Boolean(chipAnchor) && (
                    <Box
                        className='chip-hover-bridge'
                        sx={{
                            position: 'absolute',
                            bottom: -8,
                            left: 0,
                            right: 0,
                            height: 8,
                            zIndex: 1300,
                        }}
                        onMouseEnter={() => chipAnchor && setChipAnchor(chipAnchor)}
                    />
                )}

            {!isMobile && showChips && selected.length > 0 && (
                    <Popover
                        open={Boolean(chipAnchor)}
                        anchorEl={chipAnchor}
                        sx={{
                            pointerEvents: 'none',
                            '& .MuiPaper-root': {
                                pointerEvents: 'auto',
                            },
                        }}
                        onClose={() => setChipAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{
                            className: 'chip-popover',
                            onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => {
                                const related = e.relatedTarget as HTMLElement;
                                if (!related?.closest('.chip-hover-bridge, .MuiButton-root')) setChipAnchor(null);
                            },
                            sx: {
                                pointerEvents: 'auto',
                            mt: 0.5,
                            p: 1.5,
                            maxWidth: 260,
                                maxHeight: 200,
                                overflow: 'auto',
                            borderRadius: `${radii.md}px`,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                            },
                        }}>
                    <Stack spacing={0.75}>
                            {selected.map((option: any, idx: number) => {
                                const optionId = idKey ? (option as any)[idKey] : option;
                                return (
                                    <Chip
                                        key={`chip-${optionId}-${idx}`}
                                        label={getLabelValue(option)}
                                        size='small'
                                    onDelete={() => handleCheckboxChange(option as Value)}
                                    deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                                    sx={{
                                        backgroundColor: alpha(colors.primary[500], 0.1),
                                        color: colors.primary[700],
                                        borderRadius: `${radii.sm}px`,
                                        fontSize: typographyTokens.fontSize.xs,
                                        height: 26,
                                        '& .MuiChip-deleteIcon': {
                                            color: colors.primary[500],
                                            '&:hover': {
                                                color: colors.primary[700],
                                            },
                                        },
                                    }}
                                    />
                                );
                            })}
                        </Stack>
                    </Popover>
                )}

            {/* Mobile: Bottom Drawer */}
            {isMobile ? (
                <Drawer
                    anchor='bottom'
                    open={Boolean(dropdownAnchor) && !disabledWithClear}
                    onClose={() => setDropdownAnchor(null)}
                    PaperProps={{
                        sx: {
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            height: '60vh', // Fixed height
                            display: 'flex',
                            flexDirection: 'column',
                        },
                    }}>
                    {/* Drawer Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            borderBottom: `1px solid ${colors.neutral[200]}`,
                        }}>
                        <Typography
                            sx={{
                                fontSize: typographyTokens.fontSize.md,
                                fontWeight: typographyTokens.fontWeight.semibold,
                                color: colors.neutral[800],
                            }}>
                            Select {label}
                        </Typography>
                        <IconButton
                            size='small'
                            onClick={() => setDropdownAnchor(null)}
                            sx={{ color: colors.neutral[500] }}>
                            <CloseIcon fontSize='small' />
                        </IconButton>
                    </Box>

                    {/* Search */}
                    {search && (
                        <Box sx={{ p: 2, borderBottom: `1px solid ${colors.neutral[200]}`, '& > div': { width: '100%' } }}>
                            <SearchBar value={searchText} onSearch={setSearchText} />
                        </Box>
                    )}

                    {/* Options List */}
                    <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={24} sx={{ color: colors.primary[500] }} />
                            </Box>
                        ) : displayOptions.length === 0 ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 6,
                                    color: colors.neutral[500],
                                    fontSize: typographyTokens.fontSize.sm,
                                }}>
                                No items found
                            </Box>
                        ) : (
                            displayOptions.map((option, index) => {
                                const optionId = idKey ? (option as any)[idKey] : option;
                                const isChecked = Boolean(value[optionId]);
                                return (
                                    <Box
                                        key={`drawer-item-${optionId}-${index}`}
                                        onClick={() => handleCheckboxChange(option as Value)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            px: 2,
                                            py: 1.5,
                                            borderBottom: `1px solid ${colors.neutral[100]}`,
                                            cursor: 'pointer',
                                            backgroundColor: isChecked ? colors.primary[50] : 'transparent',
                                            '&:active': {
                                                backgroundColor: colors.neutral[100],
                                            },
                                        }}>
                                        <Typography
                                            sx={{
                                                fontSize: typographyTokens.fontSize.sm,
                                                color: isChecked ? colors.primary[700] : colors.neutral[700],
                                                fontWeight: isChecked ? 500 : 400,
                                            }}>
                                            {getLabelValue(option)}
                                        </Typography>
                                        {isChecked && (
                                            <CheckIcon sx={{ fontSize: 20, color: colors.primary[500] }} />
                                        )}
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                </Drawer>
            ) : (
                /* Desktop: Regular Menu */
            <Menu
                open={Boolean(dropdownAnchor) && !disabledWithClear}
                anchorEl={dropdownAnchor}
                onClose={() => setDropdownAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ 
                    sx: { 
                        width: menuWidth, 
                            maxHeight: FIXED_MENU_HEIGHT + 60,
                        mt: 0.5,
                        borderRadius: `${radii.md}px`,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        },
                }}
                MenuListProps={{ style: { padding: 0 }, ref: menuRef as any }}>
                <ClickAwayListener onClickAway={() => setDropdownAnchor(null)}>
                    <Box sx={{ width: '100%' }}>
                        {search && (
                            <Box
                                p={1.5}
                                sx={{
                                    position: 'sticky',
                                    top: 0,
                                    bgcolor: 'background.paper',
                                    zIndex: 1,
                                    borderBottom: `1px solid ${colors.neutral[200]}`,
                                }}>
                                <SearchBar width={menuWidth - 24} value={searchText} onSearch={setSearchText} />
                            </Box>
                        )}

                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, height: 100 }}>
                                <CircularProgress size={24} sx={{ color: colors.primary[500] }} />
                            </Box>
                        ) : displayOptions.length === 0 ? (
                                <Box
                                sx={{ 
                                        display: 'flex',
                                        alignItems: 'center',
                                    justifyContent: 'center', 
                                        height: FIXED_MENU_HEIGHT,
                                    color: colors.neutral[500],
                                    fontSize: typographyTokens.fontSize.sm,
                                    }}>
                                No items found
                                </Box>
                        ) : (
                                <Box sx={{ height: FIXED_MENU_HEIGHT, width: '100%' }}>
                                {displayOptions.length <= 5 ? (
                                    displayOptions.map((option, index) => {
                                        const optionId = idKey ? (option as any)[idKey] : option;
                                        const isChecked = Boolean(value[optionId]);
                                        return (
                                            <MenuItem
                                                key={`menu-item-${optionId}-${index}`}
                                                onClick={() => handleCheckboxChange(option as Value)}
                                                disableRipple
                                                sx={{ 
                                                    padding: 0,
                                                    '&:hover': {
                                                        backgroundColor: colors.neutral[100],
                                                    },
                                                }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        px: 1.5,
                                                        py: 0.5,
                                                        width: '100%',
                                                    }}>
                                                    <Checkbox
                                                        size='small'
                                                        disableRipple
                                                        checked={isChecked}
                                                        onChange={() => handleCheckboxChange(option as Value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{
                                                            color: colors.neutral[400],
                                                            '&.Mui-checked': {
                                                                color: colors.primary[500],
                                                            },
                                                        }}
                                                    />
                                                    <Typography 
                                                        noWrap
                                                        sx={{ 
                                                            fontSize: typographyTokens.fontSize.sm,
                                                            color: colors.neutral[700],
                                                        }}>
                                                        {getLabelValue(option)}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        );
                                    })
                                ) : (
                                    <FixedSizeList
                                        ref={listRef}
                                            height={FIXED_MENU_HEIGHT}
                                        width='100%'
                                        itemCount={displayOptions.length}
                                        itemSize={ITEM_HEIGHT}
                                        overscanCount={5}
                                        style={{ overflowX: 'hidden' }}>
                                        {ItemRenderer}
                                    </FixedSizeList>
                                )}
                            </Box>
                        )}
                    </Box>
                </ClickAwayListener>
            </Menu>
            )}
        </Box>
    );
};
